import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import api from '../utils/api'

interface Semester {
  id: number
  year: number
  season: string
}

interface Grade {
  id: number
  course_title: string
  instructor: string
  credits: number
  score: number
  grade: string
  year: number
  season: string
  completion_status: string
}

interface User {
  id: number
  email: string
  name: string
  birth_date?: string
  organization?: string
}

const Certificate = () => {
  const { user, isAdmin } = useAuth()
  const [semesters, setSemesters] = useState<Semester[]>([])
  const [selectedSemesters, setSelectedSemesters] = useState<string[]>([])
  const [grades, setGrades] = useState<Grade[]>([])
  const [loading, setLoading] = useState(false)
  
  // 관리자용 상태
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<User[]>([])
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [searching, setSearching] = useState(false)

  useEffect(() => {
    if (isAdmin && selectedUser) {
      loadSemestersForUser(selectedUser.id)
    } else if (!isAdmin) {
      loadSemesters()
    }
  }, [isAdmin, selectedUser])

  useEffect(() => {
    if (selectedSemesters.length > 0) {
      if (isAdmin && selectedUser) {
        loadGradesByUserAndSemesters(selectedUser.id, selectedSemesters.map(s => parseInt(s)))
      } else if (!isAdmin) {
        loadGradesBySemesters(selectedSemesters.map(s => parseInt(s)))
      }
    } else {
      setGrades([])
    }
  }, [selectedSemesters, selectedUser])

  const loadSemesters = async () => {
    try {
      // 이수 내역이 있는 학기만 조회
      const response = await api.get('/certificates/available-semesters/my')
      setSemesters(response.data)
    } catch (error) {
      console.error('학기 로드 오류:', error)
    }
  }

  const loadSemestersForUser = async (userId: number) => {
    try {
      // 관리자용: 특정 사용자의 이수 내역이 있는 학기만 조회
      const response = await api.get(`/certificates/available-semesters/user/${userId}`)
      setSemesters(response.data)
    } catch (error) {
      console.error('학기 로드 오류:', error)
    }
  }

  const searchUsers = async () => {
    if (!searchQuery.trim()) {
      alert('검색어를 입력해주세요.')
      return
    }

    try {
      setSearching(true)
      const response = await api.get(`/admin/users/search?query=${encodeURIComponent(searchQuery)}`)
      setSearchResults(response.data)
      if (response.data.length === 0) {
        alert('검색 결과가 없습니다.')
      }
    } catch (error) {
      console.error('사용자 검색 오류:', error)
      alert('사용자 검색에 실패했습니다.')
    } finally {
      setSearching(false)
    }
  }

  const selectUser = (user: User) => {
    setSelectedUser(user)
    setSearchResults([])
    setSearchQuery('')
    setSelectedSemesters([])
    setGrades([])
    // 선택된 사용자의 이수 내역이 있는 학기 로드
    loadSemestersForUser(user.id)
  }

  const toggleSemester = (semesterId: string) => {
    setSelectedSemesters(prev => {
      if (prev.includes(semesterId)) {
        return prev.filter(id => id !== semesterId)
      } else {
        return [...prev, semesterId]
      }
    })
  }

  const selectAllSemesters = () => {
    setSelectedSemesters(semesters.map(s => s.id.toString()))
  }

  const deselectAllSemesters = () => {
    setSelectedSemesters([])
  }

  const loadGradesBySemesters = async (semesterIds: number[]) => {
    try {
      setLoading(true)
      const allGrades: Grade[] = []
      
      for (const semesterId of semesterIds) {
        const response = await api.get(`/grades/my?semesterId=${semesterId}`)
        const completedGrades = response.data.filter((g: Grade) => g.completion_status === 'completed')
        allGrades.push(...completedGrades)
      }
      
      setGrades(allGrades)
    } catch (error) {
      console.error('성적 로드 오류:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadGradesByUserAndSemesters = async (userId: number, semesterIds: number[]) => {
    try {
      setLoading(true)
      const allGrades: Grade[] = []
      
      for (const semesterId of semesterIds) {
        const response = await api.get(`/admin/users/${userId}/grades?semesterId=${semesterId}`)
        const completedGrades = response.data.filter((g: Grade) => g.completion_status === 'completed')
        allGrades.push(...completedGrades)
      }
      
      setGrades(allGrades)
    } catch (error) {
      console.error('성적 로드 오류:', error)
    } finally {
      setLoading(false)
    }
  }

  const downloadCertificate = async () => {
    if (selectedSemesters.length === 0) {
      alert('학기를 선택해주세요.')
      return
    }

    if (grades.length === 0) {
      alert('이수완료된 성적이 없습니다.')
      return
    }

    try {
      let response
      const targetUser = isAdmin && selectedUser ? selectedUser : user
      const semesterIds = selectedSemesters.join(',')
      
      console.log('증명서 다운로드 요청:', {
        isAdmin,
        userId: isAdmin && selectedUser ? selectedUser.id : user?.id,
        semesterIds,
        targetUser
      })
      
      if (isAdmin && selectedUser) {
        response = await api.get(`/certificates/admin/user/${selectedUser.id}/semesters?ids=${semesterIds}`, {
          responseType: 'blob'
        })
      } else {
        response = await api.get(`/certificates/semesters?ids=${semesterIds}`, {
          responseType: 'blob'
        })
      }
      
      // 응답이 blob인지 확인
      if (response.data instanceof Blob && response.data.type === 'application/json') {
        // JSON 오류 응답인 경우
        const text = await response.data.text()
        const errorData = JSON.parse(text)
        throw new Error(errorData.error || '증명서 다운로드에 실패했습니다.')
      }
      
      const selectedSems = semesters.filter(s => selectedSemesters.includes(s.id.toString()))
      const semesterNames = selectedSems.map(s => `${s.year}년_${s.season}`).join('_')
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `이수증명서_${targetUser?.name}_${semesterNames}.pdf`)
      document.body.appendChild(link)
      link.click()
      link.remove()
    } catch (error: any) {
      console.error('증명서 다운로드 오류:', error)
      
      // blob 응답이 JSON 오류인 경우 처리
      let errorMessage = '증명서 다운로드에 실패했습니다.'
      if (error.response) {
        if (error.response.data instanceof Blob) {
          try {
            const text = await error.response.data.text()
            const errorData = JSON.parse(text)
            errorMessage = errorData.error || errorMessage
          } catch (e) {
            errorMessage = error.message || errorMessage
          }
        } else if (error.response.data?.error) {
          errorMessage = error.response.data.error
        }
      } else if (error.message) {
        errorMessage = error.message
      }
      
      console.error('상세 오류:', {
        message: errorMessage,
        status: error.response?.status,
        data: error.response?.data,
        error
      })
      
      alert(`증명서 다운로드에 실패했습니다.\n\n오류: ${errorMessage}`)
    }
  }

  const totalCredits = grades.reduce((sum, grade) => sum + grade.credits, 0)
  const currentUser = isAdmin && selectedUser ? selectedUser : user

  return (
    <div className="container">
      <h1 style={{ marginBottom: '20px' }}>이수증명서 {isAdmin ? '발급 (관리자)' : '발급'}</h1>
      
      {isAdmin && (
        <div className="card" style={{ marginBottom: '20px' }}>
          <h3 style={{ marginBottom: '15px' }}>🔍 학생 검색</h3>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && searchUsers()}
              placeholder="학생 이름 또는 이메일로 검색"
              style={{
                flex: 1,
                padding: '10px',
                borderRadius: '6px',
                border: '1px solid #ddd',
                fontSize: '16px'
              }}
            />
            <button
              onClick={searchUsers}
              disabled={searching}
              className="btn btn-primary"
              style={{ minWidth: '100px' }}
            >
              {searching ? '검색 중...' : '검색'}
            </button>
          </div>

          {searchResults.length > 0 && (
            <div style={{
              border: '1px solid #ddd',
              borderRadius: '6px',
              maxHeight: '200px',
              overflowY: 'auto'
            }}>
              {searchResults.map(u => (
                <div
                  key={u.id}
                  onClick={() => selectUser(u)}
                  style={{
                    padding: '12px',
                    borderBottom: '1px solid #eee',
                    cursor: 'pointer',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                >
                  <div style={{ fontWeight: 'bold' }}>{u.name}</div>
                  <div style={{ fontSize: '0.9em', color: '#666' }}>{u.email}</div>
                </div>
              ))}
            </div>
          )}

          {selectedUser && (
            <div style={{
              marginTop: '15px',
              padding: '15px',
              background: '#e8f5e9',
              borderRadius: '8px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <strong>선택된 학생:</strong> {selectedUser.name} ({selectedUser.email})
              </div>
              <button
                onClick={() => { setSelectedUser(null); setGrades([]); setSelectedSemesters([]); }}
                className="btn btn-secondary"
                style={{ fontSize: '0.9em', padding: '6px 12px' }}
              >
                선택 해제
              </button>
            </div>
          )}
        </div>
      )}

      {(!isAdmin || selectedUser) && (
        <div className="card" style={{ marginBottom: '20px' }}>
          <h3 style={{ marginBottom: '15px' }}>발급 정보</h3>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(2, 1fr)', 
            gap: '15px',
            padding: '15px',
            background: '#f8f9fa',
            borderRadius: '8px'
          }}>
            <div>
              <strong>이름:</strong> {currentUser?.name}
            </div>
            <div>
              <strong>생년월일:</strong> {currentUser?.birth_date || '미입력'}
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <strong>소속기관:</strong> {currentUser?.organization || '미입력'}
            </div>
          </div>

          {(!currentUser?.birth_date || !currentUser?.organization) && (
            <div className="alert alert-error" style={{ marginTop: '15px' }}>
              생년월일과 소속기관 정보가 없으면 증명서를 발급할 수 없습니다.
              {!isAdmin && ' 프로필 페이지에서 정보를 입력해주세요.'}
            </div>
          )}
        </div>
      )}

      {(!isAdmin || selectedUser) && (
        <>
          <div className="card" style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h3 style={{ margin: 0 }}>학기 선택 (복수 선택 가능)</h3>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={selectAllSemesters}
                  className="btn btn-secondary"
                  style={{ fontSize: '0.9em', padding: '6px 12px' }}
                  disabled={semesters.length === 0}
                >
                  ✓ 모두 선택
                </button>
                <button
                  onClick={deselectAllSemesters}
                  className="btn btn-secondary"
                  style={{ fontSize: '0.9em', padding: '6px 12px' }}
                  disabled={selectedSemesters.length === 0}
                >
                  ✗ 모두 해제
                </button>
              </div>
            </div>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', 
              gap: '12px',
              padding: '15px',
              background: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #ddd'
            }}>
              {semesters.map(s => (
                <label 
                  key={s.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px',
                    background: selectedSemesters.includes(s.id.toString()) ? '#e3f2fd' : 'white',
                    borderRadius: '6px',
                    border: selectedSemesters.includes(s.id.toString()) ? '2px solid #2196f3' : '1px solid #ddd',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (!selectedSemesters.includes(s.id.toString())) {
                      e.currentTarget.style.background = '#f5f5f5'
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!selectedSemesters.includes(s.id.toString())) {
                      e.currentTarget.style.background = 'white'
                    }
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedSemesters.includes(s.id.toString())}
                    onChange={() => toggleSemester(s.id.toString())}
                    style={{ cursor: 'pointer' }}
                  />
                  <span style={{ fontWeight: selectedSemesters.includes(s.id.toString()) ? 'bold' : 'normal' }}>
                    {s.year}년 {s.season}
                  </span>
                </label>
              ))}
            </div>
            {selectedSemesters.length > 0 && (
              <div style={{ marginTop: '15px', padding: '10px', background: '#e8f5e9', borderRadius: '6px' }}>
                <strong>선택된 학기:</strong> {selectedSemesters.length}개
              </div>
            )}
          </div>

          {loading ? (
            <div className="loading">로딩 중...</div>
          ) : selectedSemesters.length > 0 ? (
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3>이수 내역</h3>
                {grades.length > 0 && (
                  <button
                    onClick={downloadCertificate}
                    className="btn btn-primary"
                    disabled={!currentUser?.birth_date || !currentUser?.organization}
                  >
                    📄 증명서 다운로드
                  </button>
                )}
              </div>

              {grades.length === 0 ? (
                <p style={{ textAlign: 'center', color: '#666', padding: '30px' }}>
                  선택한 학기에 이수완료된 과목이 없습니다.
                </p>
              ) : (
                <>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>학기</th>
                        <th>과목명</th>
                        <th>교수명</th>
                        <th>성적</th>
                        <th>이수학점</th>
                      </tr>
                    </thead>
                    <tbody>
                      {grades.map(grade => (
                        <tr key={grade.id}>
                          <td>{grade.year}년 {grade.season}</td>
                          <td>{grade.course_title}</td>
                          <td>{grade.instructor}</td>
                          <td>{grade.grade}</td>
                          <td>{grade.credits}학점</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={4} style={{ textAlign: 'right', fontWeight: 'bold' }}>총 이수학점:</td>
                        <td style={{ fontWeight: 'bold', color: '#2563eb' }}>{totalCredits}학점</td>
                      </tr>
                    </tfoot>
                  </table>
                </>
              )}
            </div>
          ) : (
            <div className="card">
              <p style={{ textAlign: 'center', color: '#666', padding: '30px' }}>
                위에서 학기를 선택하면 이수 내역을 확인할 수 있습니다.
              </p>
            </div>
          )}
        </>
      )}

      {isAdmin && !selectedUser && (
        <div className="card">
          <p style={{ textAlign: 'center', color: '#666', padding: '30px' }}>
            위에서 학생을 검색하고 선택하면 이수증명서를 발급할 수 있습니다.
          </p>
        </div>
      )}
    </div>
  )
}

export default Certificate

